import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Package, MapPin, Clock, Phone, User, AlertCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import BikerLayout from '../../components/biker/BikerLayout';

const BikerDeliveries = () => {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);

  useEffect(() => {
    fetchAvailableDeliveries();
  }, []);

  const fetchAvailableDeliveries = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/bikers/available-deliveries');
      
      // Filter only deliveries (type === "delivery")
      const deliveriesOnly = response.data.rides.filter(ride => ride.type === 'delivery');
      setDeliveries(deliveriesOnly);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to load deliveries',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleAcceptDelivery = async (deliveryId) => {
    setAccepting(deliveryId);
    try {
      const api = (await import('../../utils/api')).default;
      await api.post('/bikers/delivery-action', {
        rideId: deliveryId,
        action: 'accept'
      });

      toast({
        title: 'Delivery Accepted!',
        description: 'Navigate to Active Delivery to start',
      });

      // Redirect to active delivery page or refresh
      navigate('/biker/active-delivery');
    } catch (error) {
      console.error('Error accepting delivery:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to accept delivery',
        variant: 'destructive',
      });
      setAccepting(null);
    }
  };

  if (loading) {
    return (
      <BikerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      </BikerLayout>
    );
  }

  return (
    <BikerLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Available Deliveries</h1>
            <p className="text-gray-600 mt-1">Package delivery requests near you</p>
          </div>
          <Button onClick={fetchAvailableDeliveries} variant="outline">
            Refresh
          </Button>
        </div>

        {deliveries.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <Package className="h-8 w-8 text-orange-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Deliveries Available</h2>
            <p className="text-gray-600 mb-4">
              There are no delivery requests at the moment. Make sure you're online to receive requests.
            </p>
            <Button onClick={fetchAvailableDeliveries} variant="outline">
              Refresh List
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {deliveries.map((delivery) => (
              <Card key={delivery.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <Package className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">Package Delivery</h3>
                      <p className="text-sm text-gray-600">
                        {delivery.packageType || 'Standard Package'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-orange-600">K{delivery.price}</p>
                    <p className="text-sm text-gray-600">{delivery.distance?.toFixed(1)} km</p>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">Pickup</p>
                      <p className="text-sm text-gray-900">{delivery.pickup}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <MapPin className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">Dropoff</p>
                      <p className="text-sm text-gray-900">{delivery.dropoff}</p>
                    </div>
                  </div>

                  {delivery.senderName && (
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-gray-400" />
                      <p className="text-sm text-gray-900">{delivery.senderName}</p>
                      {delivery.senderPhone && (
                        <>
                          <span className="text-gray-400">•</span>
                          <Phone className="h-4 w-4 text-gray-400" />
                          <p className="text-sm text-gray-900">{delivery.senderPhone}</p>
                        </>
                      )}
                    </div>
                  )}

                  {delivery.createdAt && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        Posted {new Date(delivery.createdAt).toLocaleTimeString()}
                      </p>
                    </div>
                  )}
                </div>

                <Button
                  onClick={() => handleAcceptDelivery(delivery.id)}
                  disabled={accepting === delivery.id}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                >
                  {accepting === delivery.id ? 'Accepting...' : 'Accept Delivery'}
                </Button>
              </Card>
            ))}
          </div>
        )}

        <Card className="p-4 bg-orange-50 border-orange-200">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-orange-900">Delivery Tips</p>
              <p className="text-sm text-orange-700 mt-1">
                You can accept one delivery at a time. Make sure you have enough fuel and confirm package details before pickup.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </BikerLayout>
  );
};

export default BikerDeliveries;
